import { Modal, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import DocumentListItem from '../../CommonComponents/DocumentListItem/DocumentListItem';
import Loader from '../../CommonComponents/Loader';
import Navbar from '../../CommonComponents/Navbar/Navbar';
import { SavedComparisonsDocChat } from '../../CommonComponents/SavedComparisons';
import { useOneTimeModal } from '../../CommonFuctions/useOneTimeModal';
import {
  DOC_CHAT,
  LOADING,
  RE_ANALYSIS,
  SUCCESS,
  WORKFLOW_INTRO_MODAL,
} from '../../Constants/commonConstants';
import { getChatHistory, uploadDocument } from '../../Redux/DocChat/docChatActions';
import { CLEAR_FAILED_DOCUMENTS } from '../../Redux/DocChat/docChatTypes';
import { useBackToID } from '../../utils/hooks/useBackToID';
import Chatbot from '../Chatbot';
import { useSelectedUploadData } from '../Chatbot/useSelectedUploadData';
import WorkflowIntroModal from '../Chatbot/WorkflowIntroModal';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const options = {
  cMapUrl: 'cmaps/',
  standardFontDataUrl: 'standard_fonts/',
};

const ViewerDocChat = () => {
  const dispatch = useDispatch();
  const { records, failedDocuments, savedDocChatRecordStatus } = useSelector(
    (state) => state.docChatData
  );
  // Activate useBackToID hook for docchat product
  useBackToID(DOC_CHAT);

  const navigate = useNavigate();
  const { modalOpen: workflowIntroModalOpen, closeModal: workflowCloseIntroModal } =
    useOneTimeModal(WORKFLOW_INTRO_MODAL);

  const [numPages, setNumPages] = useState(null);
  const {
    uuid: id,
    record: selectedRecord,
    recordType,
    selectedDoc,
    selectedDocLink,
    selectedDocLinkLoading,
    selectedDocLinkError,
  } = useSelectedUploadData();

  const error = useMemo(() => {
    if (selectedDocLinkLoading) return '';
    if (selectedDocLinkError) return 'Failed to fetch document link.';
    if (!selectedDocLink) return 'Document not found or invalid document link.';
    if (!recordType) return 'Collection or document not found.';

    return '';
  }, [selectedDocLink, recordType, selectedDocLinkLoading, selectedDocLinkError]);

  const { userData, docChatQuota } = useSelector((state) => state.loginState);
  const uploadStatus = useSelector((state) => state?.docChatData?.uploadStatus);

  useEffect(() => {
    if (!id) {
      navigate('/docchat');
    }
  }, [id]);

  useEffect(() => {
    if (uploadStatus === 'SUCCESS') {
      (async () => {
        await dispatch(getChatHistory({ userId: userData.id }));
        dispatch(uploadDocument({ loading: true }));
      })();
    }
  }, [uploadStatus]);

  useEffect(() => {
    getChatRecords();
  }, [id, userData?.id, records]);

  console.info('selectedCollection', selectedRecord);

  const getChatRecords = async () => {
    if (id && userData?.id && records?.length === 0) {
      await dispatch(getChatHistory({ userId: userData.id }));
    }
  };

  const handleDocumentLoadError = (documentLoadError) => {
    console.error('error.message', documentLoadError);
  };

  const onDocumentLoadSuccess = ({ numPages: newDocumentNumPages }) => {
    setNumPages(newDocumentNumPages);
  };

  useEffect(() => {
    // Redirect to docchat page if record is not found
    if (savedDocChatRecordStatus === SUCCESS && recordType === '') {
      navigate('/docchat');
    }
  }, [savedDocChatRecordStatus, recordType]);

  return (
    <div className='viewer-container'>
      {selectedDocLinkLoading && <Loader time='1000' type={RE_ANALYSIS} product={DOC_CHAT} />}
      <Navbar product={DOC_CHAT} />
      <section
        className={`${selectedDocLinkLoading && 'blur3px'} docchat-dashboard-container-inner`}
      >
        <SavedComparisonsDocChat
          selectedDoc={selectedDoc}
          selectedCollection={selectedRecord}
          userData={userData}
          docChatQuota={docChatQuota}
        />
        {error && !selectedDocLinkLoading && !(savedDocChatRecordStatus === LOADING) && (
          <div className='error-message'>{error}</div>
        )}
        {selectedDocLink && !error && (
          <>
            <section className='pdf-scroll-viewer'>
              <Document
                // destroy the old document instance before creating a new one to
                // avoid Cannot read properties of null (reading 'sendWithPromise') error
                key={selectedDocLink}
                file={selectedDocLink}
                options={options}
                onLoadError={handleDocumentLoadError}
                onLoadSuccess={onDocumentLoadSuccess}
              >
                {Array.from(new Array(numPages), (_, pageIndex) => (
                  <Page
                    key={`page_${pageIndex + 1}`}
                    pageNumber={pageIndex + 1}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    devicePixelRatio={1.6}
                  />
                ))}
              </Document>
            </section>
            <Chatbot selectedDoc={selectedDoc} id={id} selectedRecord={selectedRecord} />
          </>
        )}
      </section>

      <WorkflowIntroModal
        open={workflowIntroModalOpen && !selectedDocLinkLoading}
        onCtaClick={workflowCloseIntroModal}
      />

      <Modal
        title="Some files didn't get uploaded"
        open={failedDocuments.length > 0 && !selectedDocLinkLoading}
        className='file-upload-error-modal'
        onOk={() => dispatch({ type: CLEAR_FAILED_DOCUMENTS })}
        onCancel={() => dispatch({ type: CLEAR_FAILED_DOCUMENTS })}
        cancelButtonProps={{
          style: {
            display: 'none',
          },
        }}
      >
        <Typography.Text type='secondary'>
          You can still use DocChat without these files.
        </Typography.Text>
        <ul className='failed-document-list'>
          {failedDocuments?.map?.((item, index) => (
            // No ID from the backend because it failed, and this list remains unchanged.
            // No additions, deletions, or reordering occur in the list.
            // eslint-disable-next-line react/no-array-index-key
            <li key={index}>
              <DocumentListItem documentName={item.document_name} />
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  );
};

export default ViewerDocChat;
